import {
  Check,
  CircleAlert,
  Info,
  type LucideIcon,
  RefreshCw,
  TriangleAlert,
  X,
} from "lucide-react";
import type { HTMLAttributes, ReactNode } from "react";

// 알림 (DS Notice). error·warning은 role=alert, 나머지는 role=status다

export type NoticeTone = "error" | "warning" | "info" | "progress" | "success";

const TONE_CLASS: Record<NoticeTone, string> = {
  error: "st-notice--error",
  warning: "st-notice--warning",
  info: "",
  progress: "st-notice--progress",
  success: "st-notice--success",
};

const TONE_ICON: Record<NoticeTone, LucideIcon> = {
  error: CircleAlert,
  warning: TriangleAlert,
  info: Info,
  progress: RefreshCw,
  success: Check,
};

interface NoticeProps extends Omit<HTMLAttributes<HTMLDivElement>, "role"> {
  tone?: NoticeTone;
  children: ReactNode;
  /** 오른쪽 끝 버튼 하나 (예: 다시 불러오기) */
  action?: ReactNode;
  /** 있으면 닫기 버튼("알림 닫기")을 단다 */
  onClose?: () => void;
}

export function Notice({
  tone = "info",
  children,
  action,
  onClose,
  className,
  ...rest
}: NoticeProps) {
  const Icon = TONE_ICON[tone];
  const alert = tone === "error" || tone === "warning";
  return (
    <div
      role={alert ? "alert" : "status"}
      className={["st-notice", TONE_CLASS[tone], className ?? ""].filter((c) => c !== "").join(" ")}
      {...rest}
    >
      <Icon aria-hidden="true" />
      <div className="min-w-0 flex-1">{children}</div>
      {action}
      {onClose !== undefined && (
        <button
          type="button"
          aria-label="알림 닫기"
          title="알림 닫기"
          onClick={onClose}
          className="st-btn st-btn--secondary st-btn--sm st-btn--icon"
        >
          <X aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
